<script>
	import {
		setCaretPosition,
		getCaretPosition,
		getCaretCoords,
	} from "@xbs/lib-dom";
	import {
		getMatchChar,
		getTagsOptions,
		hasWhiteSpace,
		htmlEscapes,
		isDefined,
	} from "@xbs/lib-todo";
	import DatePicker from "../../../library/DatePicker.svelte";
	import Popup from "../../../library/Popup.svelte";
	import List from "../../../library/List.svelte";
	import Item from "../../Menu/Items/Item.svelte";
	import { createEventDispatcher, onMount } from "svelte";

	export let value = "";
	export let placeholder = "";
	export let tags = [];
	export let shape;
	export let editor;

	$: dropdown = editor?.dropdown;

	let self = null;
	let editableValue = null;
	let openCarretPosition = null;

	const dispatch = createEventDispatcher();

	onMount(openEditor);

	function openEditor() {
		editableValue = value;

		if (editor.targetDate) {
			openCarretPosition = editableValue.indexOf(editor.targetDate) - 2;
			setCaretPosition(self, openCarretPosition);
			openDropdown("datepicker");
		} else {
			setCaretPosition(self);
		}
	}

	function openDropdown(type) {
		const LINE_HEIGHT = 20;
		const { x, y } = getCaretCoords();

		dispatch("editing", {
			value: editableValue,
			dropdown: {
				type,
				coords: {
					x: x + window.scrollX,
					y: y + window.scrollY + LINE_HEIGHT,
				},
				options: type === "menu" ? getTagsOptions(tags, "") : [],
			},
			targetDate: editor.targetDate,
		});
	}

	function closeDropdown() {
		dispatch("editing", {
			dropdown: null,
			value: editableValue,
		});
	}

	function closeDatePicker() {
		innerValue(editableValue, "", "!");
		closeDropdown();
	}

	function handleEditorEdit() {
		editableValue = self?.textContent || "";

		dispatch("editing", {
			value: editableValue,
			dropdown: editor.dropdown,
		});
	}

	function handleKeyUp(event) {
		if (event.code === "Enter") return;

		const code = { sharp: "#", note: "!" };
		const caretPosition = getCaretPosition(self) - 1;
		const char = editableValue[caretPosition];
		const prevChar = editableValue[caretPosition - 1];
		const space = isDefined(prevChar) ? hasWhiteSpace(prevChar) : true;

		const isTag =
			(char === code["sharp"] || event.key === code["sharp"]) && space;
		const isDatePicker =
			(char === code["note"] || event.key === code["note"]) && space;

		if (isTag) {
			openDropdown("menu");
			openCarretPosition = caretPosition;
		} else if (isDatePicker) {
			openDropdown("datepicker");
			openCarretPosition = caretPosition;
		} else if (editor.dropdown) {
			const match = [];
			const space = new RegExp(/^\s*$/gm);

			for (let index = caretPosition; index > 0; index--) {
				const char = editableValue[index];

				if (char === code["sharp"]) {
					const string = match.reverse().join("");
					const options = getTagsOptions(tags, string);

					if (!options.length) break;

					dispatch("editing", {
						dropdown: { ...editor.dropdown, options },
						value: editableValue,
					});
					return;
				} else {
					match.push(char);
				}
				if (space.test(char)) break;
			}
			closeDropdown();
		}
	}

	function innerValue(string, substring, char) {
		const nextChar = string[openCarretPosition + 1];
		const compareString = string;

		substring = char + substring;

		if (!nextChar) {
			string += substring.substring(1);
		} else if (hasWhiteSpace(nextChar)) {
			const matchChar = new RegExp(String.raw`${char}\s`, "gm");
			string = string.replace(matchChar, (match, offset) => {
				if (offset === openCarretPosition) {
					return `${substring} `;
				}
				return match;
			});
		} else {
			if (char === "!") {
				const matchChar = new RegExp(/!\((.+?)\)/gm);
				string = string.replace(matchChar, (match, _, offset) => {
					if (offset === openCarretPosition) {
						return substring;
					}
					return match;
				});
			} else {
				string = string.replace(getMatchChar(char), (match, offset) => {
					if (offset === openCarretPosition) {
						return substring;
					}
					return match;
				});
			}
		}

		editableValue = string;
		self.innerHTML = htmlEscapes(string);

		setCaretPosition(
			self,
			openCarretPosition +
				(compareString !== editableValue ? substring.length : 1)
		);
		closeDropdown();
	}

	function menuItemClick(event) {
		const tag = event.detail.id;
		if (!isDefined(tag)) {
			closeDropdown();
			return;
		}
		innerValue(editableValue, tag.substring(1), "#");
	}

	function changeDate(event) {
		const date = event.detail.value;
		innerValue(editableValue, `(${date})`, "!");
	}

</script>

<div
	on:input={handleEditorEdit}
	on:keyup={handleKeyUp}
	class="wx-todo_editor"
	contenteditable="true"
	data-placeholder={placeholder}
	tabindex="0"
	bind:this={self}>
	{value}
</div>

{#if dropdown}
	{#if dropdown.type === 'menu'}
		<Popup coords={dropdown.coords} on:cancel={closeDropdown}>
			<List options={dropdown.options} on:click={menuItemClick} let:item>
				<Item {item} />
			</List>
		</Popup>
	{/if}
	{#if dropdown.type === 'datepicker'}
		<DatePicker
			dateFormat={shape.date.format}
			coords={dropdown.coords}
			cancel={closeDatePicker}
			value={editor.targetDate}
			on:change={changeDate} />
	{/if}
{/if}

<style>
	.wx-todo_editor {
		cursor: text;

		width: 100%;
		height: 100%;
		outline: none;

		word-wrap: normal;
		word-break: break-word;
		white-space: pre-wrap;
	}
	[data-placeholder]:empty:before {
		content: attr(data-placeholder);

		font-family: var(--wx-font-family);
		font-size: var(--wx-font-size);
		line-height: var(--wx-line-height);
		color: var(--wx-color-font-disabled);
	}

</style>
